import React, { Fragment, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment/moment';

import { decimalUpTo } from 'Helpers/currencyFormat';
import { formatDateWithTz, defaultDatePickerFormat } from 'Helpers/dateUtils';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableSearchInput from 'CommonComponents/TableSearchInput';

// Number of real owners shown before the "Show all" toggle expands the list.
const TOP_N = 10;

// The four status buckets, in display order. `bar` colours the stacked-bar
// segment, `value` colours the aggregate-card figure. Full literal class
// strings so Tailwind's content scanner keeps them in the build.
const BUCKETS = [
    { key: 'not_started', field: 'not_started_activities_count', label: 'Not started', bar: 'tw-bg-[#9CA3AF]', value: 'tw-text-charcoal' },
    { key: 'in_progress', field: 'in_progress_activities_count', label: 'In progress', bar: 'tw-bg-blue', value: 'tw-text-blue' },
    { key: 'overdue', field: 'overdue_activities_count', label: 'Overdue', bar: 'tw-bg-red', value: 'tw-text-red' },
    { key: 'completed', field: 'completed_activities_count', label: 'Completed', bar: 'tw-bg-green', value: 'tw-text-green' },
];

const fmt = (n) => decimalUpTo(n || 0);

const isUnallocated = (row) => row.owner_id == null;

const rowTotal = (row) =>
    (row.not_started_activities_count || 0) +
    (row.in_progress_activities_count || 0) +
    (row.overdue_activities_count || 0) +
    (row.completed_activities_count || 0);

// Aggregate counts across ALL owners (incl. Unallocated). Independent of the
// list's search/sort/windowing so the top cards never change as you filter.
function sumBuckets(owners) {
    return owners.reduce(
        (acc, row) => {
            acc.not_started += row.not_started_activities_count || 0;
            acc.in_progress += row.in_progress_activities_count || 0;
            acc.overdue += row.overdue_activities_count || 0;
            acc.completed += row.completed_activities_count || 0;
            return acc;
        },
        { not_started: 0, in_progress: 0, overdue: 0, completed: 0 },
    );
}

const matchesSearch = (row, term) => !term || (row.owner_name || '').toLowerCase().includes(term);

// Sort a copy of the owners descending by the given bucket's count.
function sortOwners(owners, bucketKey) {
    const field = BUCKETS.find((b) => b.key === bucketKey).field;
    return [...owners].sort((a, b) => (b[field] || 0) - (a[field] || 0));
}

function ownerInitials(name) {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '—';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
}

export default function ProjectDashboardSummary(props) {
    const owners = props.data || [];

    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('overdue');
    const [showAll, setShowAll] = useState(false);
    // Which owner+status drill-down is open. `ownerId: undefined` = none open
    // (distinct from Unallocated's `owner_id: null`).
    const [selected, setSelected] = useState({ ownerId: undefined, status: null });

    const totals = useMemo(() => sumBuckets(owners), [owners]);
    const grandTotal = totals.not_started + totals.in_progress + totals.overdue + totals.completed;

    // Unallocated is pinned to the top and excluded from sort/windowing; real
    // owners are searched, sorted and windowed independently.
    const unallocated = useMemo(() => owners.filter(isUnallocated), [owners]);
    const realOwners = useMemo(() => owners.filter((o) => !isUnallocated(o)), [owners]);

    const term = search.trim().toLowerCase();

    const visibleUnallocated = useMemo(() => unallocated.filter((o) => matchesSearch(o, term)), [unallocated, term]);
    const filteredRealOwners = useMemo(() => realOwners.filter((o) => matchesSearch(o, term)), [realOwners, term]);
    const sortedRealOwners = useMemo(() => sortOwners(filteredRealOwners, sortKey), [filteredRealOwners, sortKey]);

    const shownRealOwners = showAll ? sortedRealOwners : sortedRealOwners.slice(0, TOP_N);
    const displayedOwners = [...visibleUnallocated, ...shownRealOwners];

    const totalRealOwners = filteredRealOwners.length;
    const showToggle = totalRealOwners > TOP_N;

    const toggleOwnerStatus = (ownerId, status) => {
        setSelected((prev) => (prev.ownerId === ownerId && prev.status === status ? { ownerId: undefined, status: null } : { ownerId, status }));
    };

    const isEmpty = owners.length === 0 || grandTotal === 0;

    return (
        <div className="table__card">
            <div className="tw-mb-6">
                <h4 className="table__header">{$t('Activities by Owner')}</h4>
            </div>

            {props.loading ? (
                <LoadingSpinner />
            ) : (
                <Fragment>
                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-mb-6">
                        {BUCKETS.map((bucket) => (
                            <div key={`total-${bucket.key}`} className="tw-rounded-lg tw-bg-charcoal-10 tw-px-4 tw-py-3">
                                <div className="tw-text-xs tw-text-charcoal-50">{$t(bucket.label)}</div>
                                <div className={classNames('tw-text-2.5xl tw-font-semibold tw-mt-1', bucket.value)}>{fmt(totals[bucket.key])}</div>
                            </div>
                        ))}
                    </div>

                    {isEmpty ? (
                        <div className="tw-py-12 tw-px-4 tw-text-center">
                            <p className="tw-text-base tw-font-medium tw-text-charcoal">{$t('No activities yet')}</p>
                            <p className="tw-mx-auto tw-mt-2 tw-max-w-md tw-text-sm tw-text-charcoal-50">
                                {$t('Add activities to build your project plan and track progress against dates, owners and status')}
                            </p>
                            {props.canManage && (
                                <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
                                    <Button small variant="primary" onClick={props.onAddActivity}>
                                        {$t('+ Add activity')}
                                    </Button>
                                    {props.importProjectPlanUrl && (
                                        <Button small as="a" variant="secondary" href={props.importProjectPlanUrl}>
                                            {$t('Import from Excel')}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <Fragment>
                            <div className="tw-flex tw-items-center tw-gap-x-3 tw-mb-4">
                                <div className="tw-flex-1">
                                    <TableSearchInput searchValue={search} handleChange={(e) => setSearch(e.target.value)} placeholder={$t('Search owner')} />
                                </div>
                                <label htmlFor="owner-sort" className="tw-mb-0 tw-text-xs tw-text-charcoal-50">
                                    {$t('Sort')}
                                </label>
                                <select
                                    id="owner-sort"
                                    value={sortKey}
                                    onChange={(e) => setSortKey(e.target.value)}
                                    className="tw-h-8 tw-ps-3 tw-pe-8 tw-py-0 tw-rounded-full tw-border-2 tw-border-charcoal-25 tw-bg-transparent tw-text-xs tw-font-medium tw-text-charcoal focus:tw-ring-0 focus:tw-border-charcoal-25"
                                >
                                    {BUCKETS.map((bucket) => (
                                        <option key={`sort-${bucket.key}`} value={bucket.key}>
                                            {$t(bucket.label)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tw-flex tw-justify-between tw-text-2xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-px-0.5 tw-pb-2">
                                <span>{$t('Owner')}</span>
                                <span>{BUCKETS.map((b) => $t(b.label)).join(' · ')}</span>
                            </div>

                            <div>
                                {displayedOwners.map((row) => (
                                    <OwnerRow
                                        key={`owner-${row.owner_id ?? 'unallocated'}`}
                                        row={row}
                                        selected={selected}
                                        onSelect={toggleOwnerStatus}
                                        fetchProjectActivitiesUrl={props.fetchProjectActivitiesUrl}
                                        onChangeHandler={props.onChangeHandler}
                                        minDate={props.minDate}
                                        maxDate={props.maxDate}
                                    />
                                ))}
                            </div>

                            {showToggle && (
                                <div className="tw-mt-4 tw-text-xs tw-text-charcoal-50">
                                    <span>
                                        {showAll
                                            ? $t('Showing all {{m}} owners', { m: totalRealOwners })
                                            : $t('Showing top {{n}} of {{m}} owners', { n: shownRealOwners.length, m: totalRealOwners })}
                                    </span>
                                    <span> · </span>
                                    <button
                                        type="button"
                                        className="tw-text-blue tw-font-medium focus:tw-outline-none"
                                        onClick={() => setShowAll((prev) => !prev)}
                                    >
                                        {showAll ? $t('Show less') : $t('Show all ({{count}})', { count: totalRealOwners })}
                                    </button>
                                </div>
                            )}
                        </Fragment>
                    )}
                </Fragment>
            )}
        </div>
    );
}

function OwnerRow({ row, selected, onSelect, fetchProjectActivitiesUrl, onChangeHandler, minDate, maxDate }) {
    const total = rowTotal(row);
    const isOpen = selected.ownerId === row.owner_id && !!selected.status;

    return (
        <Fragment>
            <div className="tw-flex tw-items-center tw-gap-x-3 tw-py-2 tw-border-t tw-border-grey">
                <div className="tw-flex tw-w-40 tw-shrink-0 tw-items-center tw-gap-x-2">
                    <span
                        className={classNames(
                            'tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-2xs tw-font-semibold',
                            {
                                'tw-bg-charcoal-15 tw-text-charcoal-50': isUnallocated(row),
                                'tw-bg-blue-15 tw-text-blue': !isUnallocated(row),
                            },
                        )}
                    >
                        {isUnallocated(row) ? '—' : ownerInitials(row.owner_name)}
                    </span>
                    <span className="tw-truncate tw-text-sm tw-text-charcoal">{row.owner_name}</span>
                </div>

                <div className="tw-flex tw-h-4 tw-flex-1 tw-overflow-hidden tw-rounded">
                    {total === 0 ? (
                        <div className="tw-h-full tw-w-full tw-bg-charcoal-15" />
                    ) : (
                        BUCKETS.map((bucket) => {
                            const count = row[bucket.field] || 0;
                            if (count === 0) return null;
                            const width = (count / total) * 100;
                            const segmentSelected = selected.ownerId === row.owner_id && selected.status === bucket.key;
                            return (
                                <button
                                    key={`seg-${bucket.key}`}
                                    type="button"
                                    style={{ width: `${width}%` }}
                                    title={`${$t(bucket.label)}: ${fmt(count)}`}
                                    onClick={() => onSelect(row.owner_id, bucket.key)}
                                    className={classNames(bucket.bar, 'tw-h-full tw-cursor-pointer focus:tw-outline-none', {
                                        'tw-ring-2 tw-ring-inset tw-ring-charcoal': segmentSelected,
                                    })}
                                />
                            );
                        })
                    )}
                </div>

                <div className="tw-w-16 tw-text-end tw-text-xs tw-tabular-nums tw-text-charcoal-75">{fmt(total)}</div>
            </div>

            {isOpen && (
                <DetailedDataRow
                    fetchProjectActivitiesUrl={fetchProjectActivitiesUrl}
                    ownerId={selected.ownerId}
                    status={selected.status}
                    onChangeHandler={onChangeHandler}
                    minDate={minDate}
                    maxDate={maxDate}
                />
            )}
        </Fragment>
    );
}

function DetailedDataRow(props) {
    const [selectedActivityId, setSelectedActivityId] = useState(null);
    const [editingActivityId, setEditingActivityId] = useState(null);
    const [activities, setActivities] = useState();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchProjectActivity();
    }, [props.ownerId, props.status]);

    const toggleActionData = (activityId) => {
        if (activityId === selectedActivityId) {
            setSelectedActivityId(null);
        } else {
            setSelectedActivityId(activityId);
        }
    };

    const updateField = (e, key, index) => {
        let value = e ? moment(e).format('YYYY-MM-DD') : null;
        const datum = JSON.parse(JSON.stringify(activities));
        datum[index][key] = value;
        setActivities(datum);
        props.onChangeHandler(key, value, datum[index]['id']);
        setEditingActivityId(null);
    };

    const fetchProjectActivity = () => {
        setIsLoading(true);

        const params = {
            owner_id: props.ownerId,
            status: props.status,
        };

        axios
            .get(props.fetchProjectActivitiesUrl, { params })
            .then(({ data }) => {
                setActivities(data);
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div className="tw-mt-2 tw-mb-1">
            {isLoading ? (
                <LoadingSpinner />
            ) : (
                <div className="tw-bg-blue-25/50 tw-px-4 tw-py-5 tw-rounded-lg">
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    <th>{$t('Description')}</th>
                                    <th className="tw-w-28">{$t('Owner')}</th>
                                    <th className="tw-w-28">{$t('Start Date')}</th>
                                    <th className="tw-w-28">{$t('Target Completion Date')}</th>
                                    <th className="tw-w-28">{$t('Estimated Completion Date')}</th>
                                    <th className="tw-w-28">{$t('Status')}</th>
                                    <th className="tw-w-28">{$t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activities?.map((activity, index) => {
                                    return (
                                        <Fragment key={`activity-data-${index}`}>
                                            <tr>
                                                <td>{activity.description}</td>
                                                <td>{activity.owner_name || $t('Unallocated')}</td>
                                                <td className="inbud-date-cell">
                                                    {activity.start_date ? formatDateWithTz(activity.start_date, 'YYYY-MM-DD', 'DD/MM/YYYY') : '-'}
                                                </td>
                                                <td className="inbud-date-cell">
                                                    {activity.target_completion_date
                                                        ? formatDateWithTz(activity.target_completion_date, 'YYYY-MM-DD', 'DD/MM/YYYY')
                                                        : '-'}
                                                </td>
                                                {editingActivityId === activity.id && !activity.is_procurement_activity ? (
                                                    <td>
                                                        <DatePickerField
                                                            name="estimated_completion_date"
                                                            className={'form-control tw-text-center tw-cursor-pointer'}
                                                            value={activity.estimated_completion_date}
                                                            onChange={(name, value) => updateField(value, 'estimated_completion_date', index)}
                                                            dateFormat={defaultDatePickerFormat}
                                                            min={props.minDate}
                                                            max={props.maxDate}
                                                        />
                                                    </td>
                                                ) : (
                                                    <td
                                                        className={classNames('inbud-date-cell', {
                                                            'tw-cursor-pointer': !activity.is_procurement_activity,
                                                        })}
                                                        onClick={() => !activity.is_procurement_activity && setEditingActivityId(activity.id)}
                                                    >
                                                        {activity.estimated_completion_date
                                                            ? formatDateWithTz(activity.estimated_completion_date, 'YYYY-MM-DD', 'DD/MM/YYYY')
                                                            : '-'}
                                                    </td>
                                                )}
                                                <td>{activity.status_label}</td>
                                                {activity.total_actions_count > 0 ? (
                                                    <td className="tw-cursor-pointer inbud-numeric-cell" onClick={() => toggleActionData(activity.id)}>
                                                        {activity.total_actions_count}
                                                    </td>
                                                ) : (
                                                    <td className="inbud-numeric-cell">{activity.total_actions_count}</td>
                                                )}
                                            </tr>
                                            {selectedActivityId === activity.id && (
                                                <DetailedActionDataRow selectedActivity={activity} data={activity.actions} />
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailedActionDataRow(props) {
    return (
        <tr>
            <td colSpan={7}>
                <div className="tw-bg-blue-25/50 tw-px-4 tw-py-5">
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    <th>{$t('RAG Status')}</th>
                                    <th>{$t('Description')}</th>
                                    <th>{$t('Owner')}</th>
                                    <th>{$t('Due Date')}</th>
                                    <th>{$t('Completed Date')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.data.map((action, index) => {
                                    return (
                                        <tr key={`action-data-${index}`}>
                                            <td>
                                                {action?.rag_status == 'blue' && <div className="tw-w-4 tw-h-4 tw-bg-blue-500 tw-rounded-md"></div>}
                                                {action?.rag_status == 'red' && <div className="tw-w-4 tw-h-4 tw-bg-red-500 tw-rounded-md"></div>}
                                                {action?.rag_status == 'amber' && <div className="tw-w-4 tw-h-4 tw-bg-amber-500 tw-rounded-md"></div>}
                                                {action?.rag_status == 'green' && <div className="tw-w-4 tw-h-4 tw-bg-green-500 tw-rounded-md"></div>}
                                                {!action?.rag_status || (action?.rag_status == '' && '-')}
                                            </td>
                                            <td>{action.description}</td>
                                            <td>{action.owner ? action.owner?.first_name + ' ' + action.owner?.last_name : '-'}</td>
                                            <td>{action.date ? formatDateWithTz(action.date, 'YYYY-MM-DD', 'DD/MM/YYYY') : '-'}</td>
                                            <td>{action.completed_date ? formatDateWithTz(action.completed_date, 'YYYY-MM-DD', 'DD/MM/YYYY') : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </td>
        </tr>
    );
}
